import { h, Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import { format } from "date-fns";

import suggestionsApi from "../api/suggestions.ts";
import workExperiencesApi from "../api/workExperiences.ts";

import AutoSuggest from "./AutoSuggest.jsx";
import Button from "./Button.jsx";
import Checkbox from "./Checkbox.jsx";
import Datepicker from "./Datepicker.jsx";
import YearMonthPicker from "./YearMonthPicker.jsx";
import ModalForm from "./ModalForm.jsx";
import SubmitButton from "./SubmitButton.jsx";
import TextArea from "./TextArea.jsx";
import TextField from "./TextField.jsx";

import { sendError } from "../errors.js";
import { createFormData } from "../services/forms.js";
import i18n from "../i18n";

const EXPERIENCE_FORM = {
  role: {
    validations: ["required"],
  },
  company: {
    validations: ["required"],
  },
  from: {
    validations: ["required"],
  },
  to: {
    validations: [
      (_value, form) => {
        if (form.from && form.to && form.from > form.to) {
          return i18n.t("validators.from_before_to");
        }
        return true;
      },
    ],
  },
  companyCity: {
    validations: [],
  },
  description: {
    validations: [],
  },
};

export default function AddEditExperienceModal({
  experience,
  onClose,
  opened,
  afterSubmit,
  afterDelete,
}) {
  const { formData, valid, fieldsFor, onSubmit, setFormDataValue } =
    createFormData(EXPERIENCE_FORM, experience);

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [companyCitySuggestions, setCompanyCitySuggestions] = useState([]);
  const [disableScrolling, setDisableScrolling] = useState(false);

  async function handleSubmit() {
    if (!valid) return;
    setServerError(null);
    setFormErrors({});
    setSending(true);

    try {
      const result = experience
        ? await workExperiencesApi.update(experience, formData)
        : await workExperiencesApi.create(formData);
      setSuccess(true);
      setTimeout(function () {
        onClose();
      }, 500);
      afterSubmit(result);
    } catch (err) {
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        sendError(err);
        setServerError(
          i18n.t("component.add_edit_experience_modal.server_errors.save")
        );
      }
    }
    setSending(false);
  }

  async function handleDeletionClick(event) {
    event.preventDefault();

    try {
      await workExperiencesApi.delete(experience);
      onClose();
      afterDelete(experience);
    } catch (err) {
      sendError(err);
      setServerError(
        i18n.t("component.add_edit_experience_modal.server_errors.delete")
      );
    }
  }

  const roleInput = fieldsFor("role", {
    onInput: (value) => {
      suggestionsApi.roles(value).then((data) => {
        setRoleSuggestions(data.map((text) => ({ text })));
      });
    },
  });
  const companyInput = fieldsFor("company", {
    onInput: (value) => {
      suggestionsApi.companies(value).then((data) => {
        setCompanySuggestions(data.map((text) => ({ text })));
      });
    },
  });
  const companyCityInput = fieldsFor("companyCity", {
    onInput: (value) => {
      suggestionsApi.workExperienceLocations(value).then((data) => {
        setCompanyCitySuggestions(data.map((text) => ({ text })));
      });
    },
  });

  const handelCheckboxInput = (event) => {
    const { checked } = event.target;
    if (checked) {
      setFormDataValue("to", "");
    } else {
      setFormDataValue("to", format(new Date(), "yyyy-MM-dd"));
    }
  };

  function handleDatePickerOpen() {
    setDisableScrolling(true);
  }
  function handleDatePickerClose() {
    setDisableScrolling(false);
  }

  return (
    <ModalForm
      onClose={onClose}
      noOutsideClick
      title={experience ? "Edit experience" : "Add experience"}
      onSubmit={onSubmit(handleSubmit)}
      error={serverError}
      opened={opened}
      noScroll={disableScrolling}
      buttonRow={
        <Fragment>
          <SubmitButton
            sending={sending}
            success={success}
            class="mt-16"
            trackingName={experience ? "editExperience" : "createExperience"}
          >
            {i18n.t("component.add_edit_experience_modal.submit_button")}
          </SubmitButton>
          {experience && (
            <Button danger class="mt-16 mr-12" onClick={handleDeletionClick}>
              {i18n.t("component.add_edit_experience_modal.delete_button")}
            </Button>
          )}
        </Fragment>
      }
    >
      <p class="mb-40">
        <AutoSuggest
          value={roleInput.value}
          onBlur={roleInput.onBlur}
          onChange={roleInput.onInput}
          suggestions={roleSuggestions}
          errors={formErrors.role}
          inputProps={{
            ...roleInput,
            label: i18n.t("component.add_edit_experience_modal.labels.role"),
            info: i18n.t("component.add_edit_experience_modal.infos.role"),
            id: "role",
            autocomplete: "off",
          }}
        />
      </p>
      <p class="mb-40">
        <AutoSuggest
          value={companyInput.value}
          onBlur={companyInput.onBlur}
          onChange={companyInput.onInput}
          suggestions={companySuggestions}
          errors={formErrors.company}
          inputProps={{
            ...companyInput,
            errors: formErrors.company,
            label: i18n.t("component.add_edit_experience_modal.labels.company"),
            info: i18n.t("component.add_edit_experience_modal.infos.company"),
            id: "company",
            autocomplete: "off",
          }}
        />
      </p>
      <p class="mb-40">
        <TextArea
          {...fieldsFor("description", {
            serverErrors: formErrors.description,
          })}
          label={i18n.t(
            "component.add_edit_experience_modal.labels.description"
          )}
          info={i18n.t("component.add_edit_experience_modal.infos.description")}
        />
      </p>
      <p class="mb-40">
        <YearMonthPicker
          {...fieldsFor("from", { serverErrors: formErrors.from })}
          label={i18n.t("component.add_edit_experience_modal.labels.from")}
          info={i18n.t("component.add_edit_experience_modal.infos.from")}
          onOpen={handleDatePickerOpen}
          onClose={handleDatePickerClose}
        />
      </p>
      <p class="mb-40">
        <YearMonthPicker
          {...fieldsFor("to", { serverErrors: formErrors.to })}
          label={i18n.t("component.add_edit_experience_modal.labels.to")}
          info={i18n.t("component.add_edit_experience_modal.infos.to")}
          onOpen={handleDatePickerOpen}
          onClose={handleDatePickerClose}
        />
      </p>
      <p>
        <Checkbox
          class="pl-16"
          checked={!formData.to}
          onInput={handelCheckboxInput}
        >
          <span class="t-body4">
            {i18n.t("component.add_edit_experience_modal.currently_checkbox")}
          </span>
        </Checkbox>
      </p>
      <p class="mb-40">
        <AutoSuggest
          value={companyCityInput.value}
          onBlur={companyCityInput.onBlur}
          onChange={companyCityInput.onInput}
          suggestions={companyCitySuggestions}
          errors={formErrors.companyCity}
          inputProps={{
            ...companyCityInput,
            errors: formErrors.companyCity,
            label: i18n.t(
              "component.add_edit_experience_modal.labels.location"
            ),
            info: i18n.t("component.add_edit_experience_modal.infos.location"),
            id: "companyCity",
            autocomplete: "off",
          }}
        />
      </p>
    </ModalForm>
  );
}
